// ════════════════════════════════════════════════════════════════════════════
//  features/renave/VeiculoEstoqueForm.tsx — dados do veículo à venda.
//  Identificação e compra travam depois da entrada no RENAVE; o CRV pode
//  mudar até a venda (o Detran emite CRV novo ao confirmar a entrada).
// ════════════════════════════════════════════════════════════════════════════

import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ORIGEM, ORIGEM_LABEL, TIPOS_CRV, formatarCnpj, type VeiculoEstoque, type VeiculoEstoqueInput } from './api';

export interface VeiculoFormState {
  chassi: string; placa: string; renavam: string; marca: string; modelo: string;
  anoFabricacao: string; anoModelo: string; cor: string;
  tipoCrv: string; numeroCrv: string; codigoSegurancaCrv: string;
  veiculoProprio: boolean; dataCompra: string; valorCompra: string;
  quilometragemEntrada: string; dataHoraMedicaoKmEntrada: string;
  tipoDocumentoVendedor: string; documentoVendedor: string; emailVendedor: string;
  observacao: string;
  /** ORIGEM.Compra ou ORIGEM.TransferenciaEstabelecimento (filial só pelo "Receber de filial"). */
  origemEntrada: number; cnpjOrigemTransferencia: string;
}

const NONE = '__none__';

export function estadoVazio(): VeiculoFormState {
  const agora = new Date();
  const local = new Date(agora.getTime() - agora.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  return {
    chassi: '', placa: '', renavam: '', marca: '', modelo: '', anoFabricacao: '', anoModelo: '', cor: '',
    tipoCrv: NONE, numeroCrv: '', codigoSegurancaCrv: '',
    veiculoProprio: false, dataCompra: local.slice(0, 10), valorCompra: '',
    quilometragemEntrada: '', dataHoraMedicaoKmEntrada: local,
    tipoDocumentoVendedor: 'CPF', documentoVendedor: '', emailVendedor: '', observacao: '',
    origemEntrada: ORIGEM.Compra, cnpjOrigemTransferencia: '',
  };
}

export function estadoDe(v: VeiculoEstoque): VeiculoFormState {
  return {
    chassi: v.chassi, placa: v.placa ?? '', renavam: v.renavam ?? '', marca: v.marca ?? '', modelo: v.modelo ?? '',
    anoFabricacao: v.anoFabricacao?.toString() ?? '', anoModelo: v.anoModelo?.toString() ?? '', cor: v.cor ?? '',
    tipoCrv: v.tipoCrv ?? NONE, numeroCrv: v.numeroCrv ?? '', codigoSegurancaCrv: '',
    veiculoProprio: v.veiculoProprio, dataCompra: v.dataCompra?.slice(0, 10) ?? '',
    valorCompra: v.valorCompra?.toString() ?? '', quilometragemEntrada: v.quilometragemEntrada?.toString() ?? '',
    dataHoraMedicaoKmEntrada: v.dataHoraMedicaoKmEntrada?.slice(0, 16) ?? '',
    tipoDocumentoVendedor: v.tipoDocumentoVendedor ?? 'CPF', documentoVendedor: v.documentoVendedor ?? '',
    emailVendedor: v.emailVendedor ?? '', observacao: v.observacao ?? '',
    origemEntrada: v.origemEntrada ?? ORIGEM.Compra, cnpjOrigemTransferencia: v.cnpjOrigemTransferencia ?? '',
  };
}

const txt = (s: string) => (s.trim() ? s.trim() : null);
const num = (s: string) => (s.trim() ? Number(s.replace(',', '.')) : null);

export function paraInput(f: VeiculoFormState): VeiculoEstoqueInput {
  const transferencia = f.origemEntrada !== ORIGEM.Compra;
  if (transferencia) {
    // O "vendedor" de uma transferência é o estabelecimento de origem (destinatário da NF-e de compra).
    f = { ...f, veiculoProprio: false, tipoDocumentoVendedor: 'CNPJ', documentoVendedor: f.cnpjOrigemTransferencia, emailVendedor: '' };
  }
  return {
    chassi: f.chassi.trim(), placa: txt(f.placa), renavam: txt(f.renavam), marca: txt(f.marca), modelo: txt(f.modelo),
    anoFabricacao: num(f.anoFabricacao), anoModelo: num(f.anoModelo), cor: txt(f.cor),
    tipoCrv: f.tipoCrv === NONE ? null : f.tipoCrv, numeroCrv: txt(f.numeroCrv), codigoSegurancaCrv: txt(f.codigoSegurancaCrv),
    veiculoProprio: f.veiculoProprio, dataCompra: txt(f.dataCompra), valorCompra: num(f.valorCompra),
    quilometragemEntrada: num(f.quilometragemEntrada),
    // horário local de Brasília, sem fuso — como o RENAVE espera
    dataHoraMedicaoKmEntrada: f.dataHoraMedicaoKmEntrada ? `${f.dataHoraMedicaoKmEntrada}:00` : null,
    vendedorPessoaId: null,
    tipoDocumentoVendedor: f.veiculoProprio ? null : f.tipoDocumentoVendedor,
    documentoVendedor: f.veiculoProprio ? null : txt(f.documentoVendedor),
    emailVendedor: f.veiculoProprio ? null : txt(f.emailVendedor),
    observacao: txt(f.observacao),
    origemEntrada: f.origemEntrada,
    cnpjOrigemTransferencia: transferencia ? txt(f.cnpjOrigemTransferencia) : null,
  };
}

interface Props {
  valor: VeiculoFormState;
  onChange: (v: VeiculoFormState) => void;
  /** Depois da entrada no RENAVE: identificação e compra ficam só leitura. */
  identificacaoTravada?: boolean;
  possuiCodigoSegurancaCrv?: boolean;
}

export function VeiculoEstoqueForm({ valor: f, onChange, identificacaoTravada, possuiCodigoSegurancaCrv }: Props) {
  const set = <K extends keyof VeiculoFormState>(k: K, v: VeiculoFormState[K]) => onChange({ ...f, [k]: v });
  const trava = !!identificacaoTravada;
  const filial = f.origemEntrada === ORIGEM.TransferenciaFilial;
  const transferencia = f.origemEntrada !== ORIGEM.Compra;

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">Identificação</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo label="Chassi *" className="sm:col-span-2">
            <Input value={f.chassi} disabled={trava} maxLength={21} className="font-mono uppercase"
              onChange={e => set('chassi', e.target.value.toUpperCase())} />
          </Campo>
          <Campo label="Placa">
            <Input value={f.placa} disabled={trava} maxLength={8} className="font-mono uppercase"
              onChange={e => set('placa', e.target.value.toUpperCase())} />
          </Campo>
          <Campo label="RENAVAM">
            <Input value={f.renavam} disabled={trava} maxLength={11} inputMode="numeric"
              onChange={e => set('renavam', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Marca"><Input value={f.marca} maxLength={60} onChange={e => set('marca', e.target.value)} /></Campo>
          <Campo label="Modelo"><Input value={f.modelo} maxLength={80} onChange={e => set('modelo', e.target.value)} /></Campo>
          <Campo label="Ano fabricação">
            <Input value={f.anoFabricacao} maxLength={4} inputMode="numeric" onChange={e => set('anoFabricacao', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Ano modelo">
            <Input value={f.anoModelo} maxLength={4} inputMode="numeric" onChange={e => set('anoModelo', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Cor"><Input value={f.cor} maxLength={40} onChange={e => set('cor', e.target.value)} /></Campo>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">CRV (documento atual)</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo label="Tipo do CRV">
            <Select value={f.tipoCrv} onValueChange={v => set('tipoCrv', v)}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {TIPOS_CRV.map(t => <SelectItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </Campo>
          <Campo label="Número do CRV">
            <Input value={f.numeroCrv} maxLength={20} inputMode="numeric" onChange={e => set('numeroCrv', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Código de segurança do CRV"
            ajuda={possuiCodigoSegurancaCrv ? 'Já cadastrado (guardado cifrado). Preencha só para trocar.' : 'Fica guardado cifrado.'}>
            <Input value={f.codigoSegurancaCrv} maxLength={11} inputMode="numeric" autoComplete="off" type="password"
              placeholder={possuiCodigoSegurancaCrv ? '•••••••••••' : ''}
              onChange={e => set('codigoSegurancaCrv', e.target.value.replace(/\D/g, ''))} />
          </Campo>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-muted-foreground">{transferencia ? 'Transferência' : 'Compra'}</h3>
          {!transferencia && (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.veiculoProprio} disabled={trava} onCheckedChange={v => set('veiculoProprio', v)} />
              A revenda já é a proprietária no CRV
            </label>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo label="Como o veículo chega">
            {filial || trava ? (
              <Input value={ORIGEM_LABEL[f.origemEntrada] ?? ''} disabled />
            ) : (
              <Select value={String(f.origemEntrada)} onValueChange={v => set('origemEntrada', Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ORIGEM.Compra)}>{ORIGEM_LABEL[ORIGEM.Compra]}</SelectItem>
                  <SelectItem value={String(ORIGEM.TransferenciaEstabelecimento)}>{ORIGEM_LABEL[ORIGEM.TransferenciaEstabelecimento]}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Campo>
          {transferencia && (
            <Campo label="CNPJ do estabelecimento de origem *" className="sm:col-span-2"
              ajuda={filial ? undefined : 'A revenda de origem precisa ter autorizado a transferência para o CNPJ desta empresa.'}>
              {filial ? (
                <Input value={formatarCnpj(f.cnpjOrigemTransferencia)} disabled />
              ) : (
                <Input value={f.cnpjOrigemTransferencia} disabled={trava} inputMode="numeric" maxLength={14}
                  onChange={e => set('cnpjOrigemTransferencia', e.target.value.replace(/\D/g, ''))} />
              )}
            </Campo>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo label={transferencia ? 'Data da transferência' : f.veiculoProprio ? 'Data de entrada' : 'Data da compra'}>
            <Input type="date" value={f.dataCompra} disabled={trava} onChange={e => set('dataCompra', e.target.value)} />
          </Campo>
          {!f.veiculoProprio && (
            <Campo label={transferencia ? 'Valor da transferência (R$)' : 'Valor da compra (R$)'}>
              <Input value={f.valorCompra} disabled={trava} inputMode="decimal" onChange={e => set('valorCompra', e.target.value)} />
            </Campo>
          )}
          <Campo label="Quilometragem">
            <Input value={f.quilometragemEntrada} disabled={trava} inputMode="numeric"
              onChange={e => set('quilometragemEntrada', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Leitura do hodômetro em">
            <Input type="datetime-local" value={f.dataHoraMedicaoKmEntrada} disabled={trava}
              onChange={e => set('dataHoraMedicaoKmEntrada', e.target.value)} />
          </Campo>
        </div>
        {!f.veiculoProprio && !transferencia && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Campo label="Vendedor (dono no CRV)">
              <Select value={f.tipoDocumentoVendedor} disabled={trava} onValueChange={v => set('tipoDocumentoVendedor', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CPF">CPF</SelectItem>
                  <SelectItem value="CNPJ">CNPJ</SelectItem>
                </SelectContent>
              </Select>
            </Campo>
            <Campo label={f.tipoDocumentoVendedor === 'CNPJ' ? 'CNPJ do vendedor' : 'CPF do vendedor'}>
              <Input value={f.documentoVendedor} disabled={trava} inputMode="numeric"
                maxLength={f.tipoDocumentoVendedor === 'CNPJ' ? 14 : 11}
                onChange={e => set('documentoVendedor', e.target.value.replace(/\D/g, ''))} />
            </Campo>
            <Campo label="E-mail do vendedor">
              <Input type="email" value={f.emailVendedor} disabled={trava} maxLength={120}
                onChange={e => set('emailVendedor', e.target.value)} />
            </Campo>
          </div>
        )}
      </section>

      <Campo label="Observação">
        <Textarea value={f.observacao} maxLength={500} rows={2} onChange={e => set('observacao', e.target.value)} />
      </Campo>
    </div>
  );
}

/** <label> envolvendo o controle: associa o rótulo ao campo (acessibilidade) sem precisar de ids. */
function Campo({ label, ajuda, className, children }: { label: string; ajuda?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium leading-none">{label}</span>
        {children}
      </label>
      {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
    </div>
  );
}
